import type { CsvRow } from "../types/draft";
import type { PortfolioStackAnalysis } from "../services/stackDetection";
import {
  parsePickedAt,
  groupDataByPlayer,
  computePlayerCLV,
} from "./playerHelpers";
import { getTeamsTableFromCsv } from "./chartHelpers";
import { analyzePortfolioArchetypes, ARCHETYPE_LABELS } from "../services/archetypeClassifier";

/**
 * Season Wrapped metrics — a pure, dual-platform retrospective builder.
 *
 * Underdog and DraftKings are DIFFERENT markets (different ADP boards and draft
 * sizes: UD 216-pick, DK 240-pick). So:
 *  - "combine" figures pool both platforms (drafts, invested, unique players,
 *    exposure — all 12-team so exposure % is comparable).
 *  - "distinguish" figures stay per-platform and NEVER blend pick-number scales:
 *    CLV/edge is scored against each pick's own market; personality is computed
 *    per platform.
 *
 * The single input is the loaded portfolio plus a per-provider ADP selector.
 * Output is a typed metrics object PLUS an ordered `slides` list already filtered
 * by adaptive rules, so the story view is a dumb renderer.
 */

export interface WrappedInput {
  rawData: CsvRow[];
  /** Per-provider market ADP selector (from useMarketAdp). */
  lookupForProvider: (provider?: string) => Map<string, number>;
  /** Portfolio stack analysis (from context). Null when no team data. */
  portfolioStacks: PortfolioStackAnalysis | null;
}

export interface PlatformMetrics {
  provider: string;
  drafts: number;
  invested: number;
  /** Mean CLV per pick on this platform's own market. Null if unknown. */
  avgClv: number | null;
  /** Dominant construction archetype on this platform. */
  personality: { primary: string; secondary: string | null } | null;
}

export interface WrappedTopPlayer {
  name: string;
  position: string;
  team: string;
  exposurePct: number;
  /** Exposure % within each platform, keyed by provider. */
  byProvider: Record<string, number>;
}

export interface WrappedSteal {
  name: string;
  position: string;
  pick: number;
  marketAdp: number;
  clv: number;
  provider: string;
}

export type WrappedSlide =
  | { type: "intro" }
  | { type: "platformSplit" }
  | { type: "drafts" }
  | { type: "players" }
  | { type: "player" }
  | { type: "posLean" }
  | { type: "markets" }
  | { type: "steal" }
  | { type: "stack" }
  | { type: "personality" }
  | { type: "temporal" }
  | { type: "summary" };

export interface WrappedMetrics {
  hasData: boolean;
  isThin: boolean;
  seasonWindow: { startLabel: string; endLabel: string } | null;
  combined: { drafts: number; invested: number; uniquePlayers: number };
  platforms: PlatformMetrics[];
  hasMultiplePlatforms: boolean;
  topPlayer: WrappedTopPlayer | null;
  posLean: Array<{ pos: string; pct: number }>;
  signatureStack: { a: string; b: string; count: number; nflTeam: string } | null;
  biggestSteal: WrappedSteal | null;
  temporal: { peakMonthLabel: string; peakCount: number; months: Array<{ label: string; count: number }> } | null;
  slides: WrappedSlide[];
}

const KNOWN_PROVIDERS = ["Underdog", "DraftKings"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const POS_KEYS = ["QB", "RB", "WR", "TE"];

function rowProvider(row: CsvRow): string {
  return (row["Provider"] || "").trim() || "Unknown";
}

function entryFee(row: CsvRow): number {
  const fee = parseFloat(row["Tournament Entry Fee"] ?? row["Draft Entry Fee"] ?? "");
  return isNaN(fee) ? 0 : fee;
}

/** Sum of entry fee per UNIQUE draft in `rows` (fee is per entry, deduped). */
function investedFor(rows: CsvRow[]): number {
  const seen = new Set<string>();
  let total = 0;
  for (const r of rows) {
    const d = r["Draft"];
    if (d && !seen.has(d)) {
      seen.add(d);
      total += entryFee(r);
    }
  }
  return total;
}

function uniqueDrafts(rows: CsvRow[]): number {
  return new Set(rows.map(r => r["Draft"])).size;
}

/** Mean CLV across `rows`, each pick scored on its own platform's market. */
function avgClvFor(rows: CsvRow[], lookupForProvider: (p?: string) => Map<string, number>): number | null {
  let sum = 0;
  let n = 0;
  for (const r of rows) {
    const pick = Number(r["Pick Number"]);
    if (isNaN(pick)) continue;
    const name = `${r["First Name"] ?? ""} ${r["Last Name"] ?? ""}`.trim();
    const { clv } = computePlayerCLV(name, pick, lookupForProvider(rowProvider(r)));
    if (typeof clv === "number") { sum += clv; n += 1; }
  }
  return n > 0 ? sum / n : null;
}

function personalityFor(rows: CsvRow[]): { primary: string; secondary: string | null } | null {
  if (rows.length === 0) return null;
  const teams = getTeamsTableFromCsv(rows);
  if (teams.length === 0) return null;
  const analysis = analyzePortfolioArchetypes(teams);
  if (!analysis.dominantArchetype) return null;
  return { primary: ARCHETYPE_LABELS[analysis.dominantArchetype] ?? "Balanced", secondary: null };
}

function monthLabel(d: Date): string {
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export function buildWrappedMetrics(input: WrappedInput): WrappedMetrics {
  const { rawData, lookupForProvider, portfolioStacks } = input;

  const empty: WrappedMetrics = {
    hasData: false,
    isThin: false,
    seasonWindow: null,
    combined: { drafts: 0, invested: 0, uniquePlayers: 0 },
    platforms: [],
    hasMultiplePlatforms: false,
    topPlayer: null,
    posLean: [],
    signatureStack: null,
    biggestSteal: null,
    temporal: null,
    slides: [],
  };

  if (!rawData || rawData.length === 0) return empty;

  // ── Platform split ────────────────────────────────────────────────
  const rowsByProvider: Record<string, CsvRow[]> = {};
  for (const r of rawData) (rowsByProvider[rowProvider(r)] ??= []).push(r);
  const presentKnown = KNOWN_PROVIDERS.filter(p => rowsByProvider[p]?.length);
  // Include a legacy "Unknown" slice as its own platform only if it's the sole one.
  const platformKeys = presentKnown.length > 0 ? presentKnown : Object.keys(rowsByProvider);

  const platforms: PlatformMetrics[] = platformKeys.map(prov => {
    const rows = rowsByProvider[prov] ?? [];
    return {
      provider: prov,
      drafts: uniqueDrafts(rows),
      invested: investedFor(rows),
      avgClv: avgClvFor(rows, lookupForProvider),
      personality: personalityFor(rows),
    };
  });
  const hasMultiplePlatforms = platforms.length > 1;

  // ── Combined totals ───────────────────────────────────────────────
  const totalDrafts = uniqueDrafts(rawData);
  const totalInvested = investedFor(rawData);
  const players = groupDataByPlayer(rawData);
  const combined = { drafts: totalDrafts, invested: totalInvested, uniquePlayers: players.length };
  const isThin = totalDrafts <= 2;

  // ── Season window ─────────────────────────────────────────────────
  let minDate: Date | null = null;
  let maxDate: Date | null = null;
  for (const r of rawData) {
    const d = parsePickedAt(r["Picked At"]);
    if (!d) continue;
    if (!minDate || d < minDate) minDate = d;
    if (!maxDate || d > maxDate) maxDate = d;
  }
  const fmt = (d: Date) => `${MONTHS[d.getMonth()]} ${d.getDate()}`;
  const seasonWindow = minDate && maxDate ? { startLabel: fmt(minDate), endLabel: fmt(maxDate) } : null;

  // ── Top (most-exposed) player, with per-platform split ────────────
  let topPlayer: WrappedTopPlayer | null = null;
  {
    const providerDraftTotals: Record<string, number> = {};
    for (const p of platforms) providerDraftTotals[p.provider] = p.drafts;
    let best: WrappedTopPlayer | null = null;
    for (const pl of players) {
      const playerDrafts = new Set(pl.rows.map(r => r["Draft"])).size;
      const exposurePct = totalDrafts > 0 ? (playerDrafts / totalDrafts) * 100 : 0;
      if (best && exposurePct <= best.exposurePct) continue;
      const byProvider: Record<string, number> = {};
      for (const p of platforms) {
        const provRows = pl.rows.filter(r => rowProvider(r) === p.provider);
        const provDrafts = new Set(provRows.map(r => r["Draft"])).size;
        byProvider[p.provider] = providerDraftTotals[p.provider] > 0
          ? (provDrafts / providerDraftTotals[p.provider]) * 100 : 0;
      }
      best = { name: pl.name, position: pl.position, team: pl.team, exposurePct, byProvider };
    }
    topPlayer = best;
  }

  // ── Positional lean (share among QB/RB/WR/TE) ─────────────────────
  const posCounts: Record<string, number> = {};
  let posTotal = 0;
  for (const r of rawData) {
    const pos = (r["Position"] || "").trim().toUpperCase();
    if (POS_KEYS.includes(pos)) { posCounts[pos] = (posCounts[pos] || 0) + 1; posTotal += 1; }
  }
  const posLean = POS_KEYS
    .map(pos => ({ pos, pct: posTotal > 0 ? Math.round((posCounts[pos] || 0) / posTotal * 100) : 0 }))
    .filter(p => p.pct > 0)
    .sort((a, b) => b.pct - a.pct);

  // ── Signature stack (from portfolio stack analysis) ───────────────
  let signatureStack: WrappedMetrics["signatureStack"] = null;
  const topPair = portfolioStacks?.topPairings?.[0];
  if (topPair && topPair.count > 0) {
    signatureStack = { a: topPair.qbName, b: topPair.receiverName, count: topPair.count, nflTeam: topPair.nflTeam };
  }

  // ── Biggest steal (max positive CLV pick, own market) ─────────────
  let biggestSteal: WrappedSteal | null = null;
  for (const r of rawData) {
    const pick = Number(r["Pick Number"]);
    if (isNaN(pick)) continue;
    const name = `${r["First Name"] ?? ""} ${r["Last Name"] ?? ""}`.trim();
    const prov = rowProvider(r);
    const { clv, udAdp } = computePlayerCLV(name, pick, lookupForProvider(prov));
    if (typeof clv === "number" && clv > 0 && (!biggestSteal || clv > biggestSteal.clv)) {
      biggestSteal = {
        name,
        position: (r["Position"] || "").trim().toUpperCase(),
        pick,
        marketAdp: Math.round(udAdp ?? 0),
        clv,
        provider: prov,
      };
    }
  }

  // ── Temporal (busiest month by unique drafts) ─────────────────────
  let temporal: WrappedMetrics["temporal"] = null;
  {
    const draftFirstDate = new Map<string, Date>();
    for (const r of rawData) {
      const d = parsePickedAt(r["Picked At"]);
      const draft = r["Draft"];
      if (!d || !draft) continue;
      const prev = draftFirstDate.get(draft);
      if (!prev || d < prev) draftFirstDate.set(draft, d);
    }
    if (draftFirstDate.size > 0) {
      const byMonth = new Map<string, { label: string; count: number; sort: number }>();
      for (const d of draftFirstDate.values()) {
        const key = `${d.getFullYear()}-${d.getMonth()}`;
        const entry = byMonth.get(key) ?? { label: monthLabel(d), count: 0, sort: d.getFullYear() * 12 + d.getMonth() };
        entry.count += 1;
        byMonth.set(key, entry);
      }
      const months = [...byMonth.values()].sort((a, b) => a.sort - b.sort).map(m => ({ label: m.label, count: m.count }));
      const peak = months.reduce((mx, m) => (m.count > mx.count ? m : mx), months[0]);
      temporal = { peakMonthLabel: peak.label, peakCount: peak.count, months };
    }
  }

  const metrics: WrappedMetrics = {
    hasData: true,
    isThin,
    seasonWindow,
    combined,
    platforms,
    hasMultiplePlatforms,
    topPlayer,
    posLean,
    signatureStack,
    biggestSteal,
    temporal,
    slides: [],
  };
  metrics.slides = buildSlides(metrics);
  return metrics;
}

/** Ordered slide list with adaptive drops. Always yields ≥ 3 slides. */
export function buildSlides(m: WrappedMetrics): WrappedSlide[] {
  const slides: WrappedSlide[] = [{ type: "intro" }];

  if (m.hasMultiplePlatforms) slides.push({ type: "platformSplit" });
  else slides.push({ type: "drafts" });

  slides.push({ type: "players" });
  if (m.topPlayer) slides.push({ type: "player" });
  if (m.posLean.length > 0) slides.push({ type: "posLean" });

  // "Edge by market" only distinguishes when there are two platforms with CLV.
  if (m.hasMultiplePlatforms && m.platforms.some(p => p.avgClv !== null)) {
    slides.push({ type: "markets" });
  }
  if (m.biggestSteal) slides.push({ type: "steal" });
  if (m.signatureStack) slides.push({ type: "stack" });
  if (m.platforms.some(p => p.personality)) slides.push({ type: "personality" });
  if (m.temporal && !m.isThin && m.combined.drafts >= 4) slides.push({ type: "temporal" });

  slides.push({ type: "summary" });
  return slides;
}
